import { SalarySensitiveValue } from "@/components/sensitive-value/SalarySensitiveValue.tsx";
import {
  ConfirmationModal,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Title,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { PendingTableCell } from "@/pages/employees/employee/tables/PendingTableCell.tsx";
import {
  createExpandableTextCell,
  createHistoricalDateCell,
  createTextCell,
} from "@/pages/employees/employee/tables/table-cell-factory.utils.tsx";
import { EmployeeCompensationEditModal } from "@/pages/employees/employee/widgets/compensation/EmployeeCompensationEditModal.tsx";
import {
  useEmployeeCompensationDelete,
  useEmployeeCompensationsSearch,
} from "@/queries/employees/use-employee-compensations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiEmployeeCompensation,
  ApiEmployeeCompensationPendingRow,
  getEmployeeCompensationPerTypeTitle,
} from "@/types/employees/employee-compensation.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type CompensationRow =
  (ApiEmployeeCompensation & { pending?: never }) | ApiEmployeeCompensationPendingRow;
const compensationColumnHelper = createColumnHelper<CompensationRow>();

export const EmployeeWorkInfoCompensations = ({
  employee,
  tableScrollBoundaryRef,
}: EmployeePageComponentProps) => {
  const { t } = useTranslation();
  const { canEditField, canViewField, canViewAnyOfFields, canViewCustomTableFieldForEmployee } =
    usePermissions();
  const { renderDate, renderCurrency } = useFormatter();
  const { openModal } = useModals();
  const [page, setPage] = useState(1);
  const { table, fields } = useSystemTableConfig(ApiTableTypes.employee_compensations, "view");
  const { renderFieldDataInCell } = useCustomFieldsHook();

  const canViewHistory = useMemo(
    () => canViewAnyOfFields(employee, [EmployeeFieldPermission.employee_compensations_history]),
    [employee, canViewAnyOfFields]
  );
  const canManageCompensation = useMemo(
    () => canEditField(employee, EmployeeFieldPermission.manage_employee_compensation),
    [employee, canEditField]
  );
  const canViewCompensationLines = canViewField(
    employee,
    EmployeeFieldPermission.employee_compensation_lines
  );

  const {
    data: employeeCompensations,
    isLoading,
    isFetching,
  } = useEmployeeCompensationsSearch(employee.id, page, canViewHistory);

  const employeeCompensationDelete = useEmployeeCompensationDelete();
  const isCurrentCompensation = useCallback(
    (compensation: CompensationRow) =>
      !compensation.pending && compensation.id === employee.employee_compensation?.id,
    [employee.employee_compensation?.id]
  );

  const getTextClassName = useCallback(
    (compensation: CompensationRow) =>
      isCurrentCompensation(compensation) ? "text-title" : "text-secondary",
    [isCurrentCompensation]
  );

  const lastCurrencyInData = employeeCompensations?.items[0]?.currency;

  const displayData = useMemo<CompensationRow[]>(
    () => [
      ...(page === 1 ? (employeeCompensations?.pending_rows ?? []) : []),
      ...(employeeCompensations?.items ?? []),
    ],
    [employeeCompensations, page]
  );

  const compensationRows = useMemo(
    () =>
      canViewHistory
        ? displayData
        : employee.employee_compensation
          ? [employee.employee_compensation]
          : [],
    [canViewHistory, displayData, employee.employee_compensation]
  );

  const usedCompensationComponents = useMemo(() => {
    const componentsById = new Map(
      compensationRows.flatMap((compensation) =>
        (compensation.lines ?? []).map((line) => [line.component.id, line.component] as const)
      )
    );

    return [...componentsById.values()].sort((first, second) => first.sort - second.sort);
  }, [compensationRows]);

  const handleAdd = useCallback(() => {
    openModal(EmployeeCompensationEditModal, {
      employee,
      lastCurrency: lastCurrencyInData,
    });
  }, [employee, lastCurrencyInData, openModal]);

  const handleEdit = useCallback(
    (employeeCompensation: ApiEmployeeCompensation) => {
      if (!canManageCompensation) return;

      openModal(EmployeeCompensationEditModal, {
        employee,
        employeeCompensation,
      });
    },
    [canManageCompensation, employee, openModal]
  );

  const handleDeleteAction = useCallback(
    (employeeCompensation: ApiEmployeeCompensation) => {
      if (!canManageCompensation) return;

      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          employeeCompensationDelete.mutate(
            { employeeId: employee.id, employeeCompensationId: employeeCompensation.id },
            {
              onSuccess: () => {
                analyticsService.trackEvent(analyticEvents.employeeCompensations.deleted, {
                  [analyticProperties.id]: employee.id,
                });
              },
            }
          );
        },
      });
    },
    [canManageCompensation, employee, openModal, employeeCompensationDelete, t]
  );

  const columns = useMemo<ColumnDef<CompensationRow, unknown>[]>(() => {
    const inner: ColumnDef<CompensationRow, unknown>[] = [];

    if (canViewField(employee, EmployeeFieldPermission.employee_compensation_date)) {
      inner.push(
        compensationColumnHelper.display({
          header: t("common:date"),
          cell: (cell) => {
            const row = cell.row.original;
            if (row.pending) {
              return (
                <PendingTableCell pending={row.pending}>
                  {row.date_effective_from ? renderDate(row.date_effective_from) : "-"}
                </PendingTableCell>
              );
            }
            return createHistoricalDateCell<CompensationRow>({
              getValue: (comp) =>
                comp.date_effective_from ? renderDate(comp.date_effective_from) : null,
              getClassName: getTextClassName,
              getDate: (comp) => comp.date_effective_from,
            })(cell);
          },
          id: ApiFieldTypes.effective_from,
          ...ColumnSizes.historicalDate,
        })
      );
    }

    if (canViewCompensationLines) {
      usedCompensationComponents.forEach((component) => {
        inner.push(
          compensationColumnHelper.display({
            header: component.title,
            cell: ({ row }) => {
              const lines = (row.original.lines ?? []).filter(
                (line) => line.compensation_component_id === component.id
              );

              if (lines.length === 0) return "-";

              const content = (
                <Stack gap="sm">
                  {lines.map((line, lineIndex) => {
                    const employeeReceives =
                      line.amount_net == null
                        ? null
                        : renderCurrency(line.amount_net, line.currency_code);
                    const companySpends =
                      line.amount_gross == null
                        ? null
                        : renderCurrency(line.amount_gross, line.currency_code);
                    const cellAmount = line.amount_gross ?? line.amount_net;
                    const cellValue =
                      cellAmount == null ? null : renderCurrency(cellAmount, line.currency_code);

                    return (
                      <Stack key={`${line.id}-${lineIndex}`}>
                        <Stack direction={"horizontal"}>
                          <Tooltip>
                            <TooltipTrigger>
                              <SalarySensitiveValue
                                value={cellValue}
                                textClassName={getTextClassName(row.original)}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <Stack gap="xs" className={"text-sm"}>
                                <Text>{component.title}</Text>
                                <Stack direction="horizontal" gap="sm" items={"center"}>
                                  <Text>{t("core:compensation_line_net")}</Text>
                                  <SalarySensitiveValue value={employeeReceives} />
                                </Stack>
                                <Stack direction="horizontal" gap="sm" items={"center"}>
                                  <Text>{t("core:compensation_line_gross")}</Text>
                                  <SalarySensitiveValue value={companySpends} />
                                </Stack>
                              </Stack>
                            </TooltipContent>
                          </Tooltip>
                        </Stack>

                        <Text className="text-secondary">
                          {getEmployeeCompensationPerTypeTitle(line.per_type)}
                        </Text>
                      </Stack>
                    );
                  })}
                </Stack>
              );

              return row.original.pending ? (
                <PendingTableCell pending={row.original.pending}>{content}</PendingTableCell>
              ) : (
                content
              );
            },
            id: `compensation_component_${component.id}`,
            ...ColumnSizes.middle,
          })
        );
      });
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_compensation_change_reason)) {
      inner.push(
        compensationColumnHelper.display({
          header: t("core:compensation_change_reason"),
          cell: createTextCell<CompensationRow>({
            getValue: (comp) => comp.change_reason?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_compensation_change_reason,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_compensation_comment)) {
      inner.push(
        compensationColumnHelper.display({
          header: t("common:comments"),
          cell: createExpandableTextCell<CompensationRow>({
            getValue: (comp) => comp.comment,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_compensation_comment,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_compensation_date)) {
      inner.push(
        compensationColumnHelper.display({
          header: t("common:date_effective_to"),
          cell: createTextCell<CompensationRow>({
            getValue: (compensation) =>
              compensation.date_effective_to ? renderDate(compensation.date_effective_to) : null,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.effective_to,
          ...ColumnSizes.standard,
        })
      );
    }

    const configColumns = fields.flatMap((field) => {
      if (field.type === ApiFieldTypes.custom) {
        if (!table || !canViewCustomTableFieldForEmployee(employee, table.id, field.slug)) {
          return [];
        }
        return [
          compensationColumnHelper.display({
            header: field.title,
            cell: ({ row }) => renderFieldDataInCell(field, row.original.custom_fields ?? []),
            id: field.slug,
            ...ColumnSizes.standard,
          }),
        ];
      }
      if (
        field.type === ApiFieldTypes.changed_by &&
        canViewField(employee, EmployeeFieldPermission.employee_compensation_changed_by)
      ) {
        return [
          compensationColumnHelper.display({
            header: field.title?.trim() || t("core:changed_by"),
            cell: ({ row }) =>
              row.original.changed_by ? (
                <EmployeeHoverCard
                  employee={row.original.changed_by}
                  position={getEmployeePositionTitle(row.original.changed_by)}
                >
                  <EmployeeItem employee={row.original.changed_by} showPosition={false} size="sm" />
                </EmployeeHoverCard>
              ) : null,
            id: field.type,
            ...ColumnSizes.standard,
          }),
        ];
      }
      if (field.type === ApiFieldTypes.employee_compensation_lines) {
        return inner.filter((item) => String(item.id).startsWith("compensation_component_"));
      }
      const column = inner.find((item) => item.id === field.type);
      const fallbackTitle = typeof column?.header === "string" ? column.header : "";
      return column ? [{ ...column, header: field.title?.trim() || fallbackTitle }] : [];
    });

    return configColumns;
  }, [
    employee,
    canViewField,
    canViewCompensationLines,
    getTextClassName,
    renderDate,
    renderCurrency,
    usedCompensationComponents,
    t,
    fields,
    table,
    canViewCustomTableFieldForEmployee,
    renderFieldDataInCell,
  ]);

  const renderRowAction = useCallback(
    (row: Row<CompensationRow>) => {
      if (row.original.pending) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => handleEdit(row.original as ApiEmployeeCompensation)}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => handleDeleteAction(row.original as ApiEmployeeCompensation)}
            variant="menuDanger"
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [handleEdit, handleDeleteAction, t]
  );

  const displayCount = useMemo(() => {
    if (!canViewHistory) return 1;
    return employeeCompensations?.metadata.total_items || 0;
  }, [canViewHistory, employeeCompensations?.metadata]);

  return (
    <Stack gap="lg">
      <Stack
        className="justify-between"
        direction="horizontal"
        items="center"
        gap="md"
        data-employee-table-heading
      >
        <Title
          header={t("core:compensation")}
          size="lg"
          badge={displayCount > 1 ? `(${displayCount})` : undefined}
          badgeVariant="secondary"
        />
        {canManageCompensation && (
          <Button
            onClick={() => handleAdd()}
            variant="secondary"
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:add_record")}
          </Button>
        )}
      </Stack>

      <HistoryTable<CompensationRow>
        currentItem={employee.employee_compensation ? employee.employee_compensation : undefined}
        historyItems={displayData}
        columns={columns}
        canViewHistory={canViewHistory}
        isLoading={isLoading}
        isFetching={isFetching || employeeCompensationDelete.isPending}
        pagination={{
          metadata: employeeCompensations?.metadata,
          currentPage: page,
          onPageChange: setPage,
        }}
        emptyStateTitle={t("core:no_compensation_assigned")}
        scrollLayout="notion"
        scrollBoundaryRef={tableScrollBoundaryRef}
        stickyLastColumn={false}
        renderRowAction={canManageCompensation ? renderRowAction : undefined}
      />
    </Stack>
  );
};
